import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { BehaviorSubject } from 'rxjs';
import { take } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class NotificationsService {
  private hubConnection: signalR.HubConnection | null = null;
  private notificationsSubject = new BehaviorSubject<any>(null);
  private seenSubject = new BehaviorSubject<number | null>(null);
  private errorSubject = new BehaviorSubject<string | null>(null);
  private connectionStateSubject = new BehaviorSubject<string>('Disconnected');

  notifications$ = this.notificationsSubject.asObservable();
  notificationSeen$ = this.seenSubject.asObservable();
  error$ = this.errorSubject.asObservable();
  connectionState$ = this.connectionStateSubject.asObservable();

  constructor(private oidcSecurityService: OidcSecurityService, private http: HttpClient) { }

  async startConnection() {
    try {
      if (this.hubConnection && this.hubConnection.state !== signalR.HubConnectionState.Disconnected) {
        await this.stopConnection();
      }

      this.hubConnection = new signalR.HubConnectionBuilder()
        .withUrl(`/notificationHub`, {
          accessTokenFactory: async () =>
            (await this.oidcSecurityService.getAccessToken().pipe(take(1)).toPromise()) || '',
          transport: signalR.HttpTransportType.LongPolling
        })
        .withAutomaticReconnect({
          nextRetryDelayInMilliseconds: retryContext => {
            if (retryContext.previousRetryCount === 0) return 0;
            if (retryContext.previousRetryCount < 3) return 2000;
            if (retryContext.previousRetryCount < 5) return 10000;
            return 30000;
          }
        })
        .configureLogging(signalR.LogLevel.Information)
        .build();

      // Event handlers for connection lifecycle
      this.hubConnection.onclose(() => {
        this.connectionStateSubject.next('Disconnected');
      });

      this.hubConnection.onreconnecting(() => {
        this.connectionStateSubject.next('Reconnecting');
      });

      this.hubConnection.onreconnected(() => {
        this.connectionStateSubject.next('Connected');
        // Re-join user group after reconnection
        this.hubConnection?.invoke('JoinUserGroup').catch(err =>
          console.error('Error rejoining:', err)
        );
      });

      // Start connection
      this.hubConnection
        .start()
        .then(() => {
          this.connectionStateSubject.next('Connected');
          return this.hubConnection?.invoke('JoinUserGroup');
        })
        .catch(err => {
          this.connectionStateSubject.next('Failed');
          console.error('SignalR Connection Error:', err);
          this.errorSubject.next(err.message || 'Failed to connect to notifications');
        });

      // Event listeners
      this.hubConnection.on('NewNotification', (notification) => {
        this.notificationsSubject.next(notification);
      });

      this.hubConnection.on('NotificationMarkedAsSeen', (notificationId: number) => {
        this.seenSubject.next(notificationId);
      });

    } catch (error: any) {
      this.connectionStateSubject.next('Failed');
      this.errorSubject.next(error.message || 'Error initializing SignalR connection');
    }
  }

  stopConnection() {
    if (this.hubConnection) {
      this.hubConnection.stop().then(() => {
        this.connectionStateSubject.next('Disconnected');
      });
    }
  }

  async markAsRead(notificationId: number): Promise<void> {
    if (this.hubConnection && this.hubConnection.state === signalR.HubConnectionState.Connected) {
      try {
        await this.hubConnection.invoke('MarkNotificationAsSeen', notificationId);
      } catch (error: any) {
        this.errorSubject.next('Failed to mark notification as read');
      }
    } else {
      this.errorSubject.next('Not connected to notification service');
    }
  }

  async sendTestNotification(message: string): Promise<void> {
    if (this.hubConnection && this.hubConnection.state === signalR.HubConnectionState.Connected) {
      try {
        await this.hubConnection.invoke('SendTestNotification', message);
      } catch (error: any) {
        this.errorSubject.next('Failed to send test notification');
      }
    }
  }

  getConnectionState(): string {
    return this.hubConnection?.state || 'Disconnected';
  }

  isConnected(): boolean {
    return this.hubConnection?.state === signalR.HubConnectionState.Connected;
  }

  async getNotifications(): Promise<void> {
    try {
      const url = `${environment.apiEndpoint}v1/Notifications/user-notifications`;
      const response = await this.http.get<any>(url).toPromise();
      this.notificationsSubject.next(response.data);
    } catch (error: any) {
      this.errorSubject.next(error.message || 'Error fetching unread notifications');
    }
  }

  async markNotificationAsSeen(notificationId: number): Promise<void> {
    try {
      const url = `${environment.apiEndpoint}v1/Notifications/mark-as-seen`;
      const body = { notificationId };
      await this.http.post<any>(url, body).toPromise();
      this.seenSubject.next(notificationId);
    } catch (error: any) {
      this.errorSubject.next(error.message || 'Error marking notification as seen');
    }
  }
}